import { useMemo, useState } from 'react';
import { useRouteInterests, type GlobalRouteInterest } from '@/hooks/useRouteInterests';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Download, Loader2, Users, UserPlus, UserCheck, CalendarClock, GraduationCap, Search } from 'lucide-react';

const PAGE_SIZE = 50;

type AccountFilter = 'all' | 'registered' | 'visitor';
type RangeFilter = 'all' | '7' | '30' | '90';

function toCsv(rows: GlobalRouteInterest[]): string {
  const header = ['Nombre', 'Correo', 'Celular', 'Ruta', 'Empresa', 'Cuenta', 'Fecha'];
  const esc = (v: string) => `"${String(v).replace(/"/g, '""')}"`;
  const lines = rows.map(r =>
    [
      r.full_name ?? '',
      r.email,
      r.phone ?? '',
      r.program_name,
      r.organization_name ?? 'Independiente',
      r.user_id ? 'Registrado' : 'Visitante',
      new Date(r.created_at).toLocaleString('es-CO'),
    ]
      .map(esc)
      .join(','),
  );
  return [header.map(esc).join(','), ...lines].join('\n');
}

export default function RouteInterestsPage() {
  const { data, isLoading } = useRouteInterests();
  const rows = useMemo(() => data ?? [], [data]);

  const [search, setSearch] = useState('');
  const [programId, setProgramId] = useState('all');
  const [orgId, setOrgId] = useState('all');
  const [account, setAccount] = useState<AccountFilter>('all');
  const [range, setRange] = useState<RangeFilter>('all');
  const [page, setPage] = useState(0);

  const programOptions = useMemo(() => {
    const map = new Map<string, string>();
    rows.forEach(r => map.set(r.program_id, r.program_name));
    return Array.from(map, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [rows]);

  const orgOptions = useMemo(() => {
    const map = new Map<string, string>();
    rows.forEach(r => {
      if (r.organization_id && r.organization_name) map.set(r.organization_id, r.organization_name);
    });
    return Array.from(map, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [rows]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const cutoff = range === 'all' ? null : Date.now() - Number(range) * 24 * 60 * 60 * 1000;
    return rows.filter(r => {
      if (term && !`${r.full_name ?? ''} ${r.email} ${r.phone ?? ''}`.toLowerCase().includes(term)) return false;
      if (programId !== 'all' && r.program_id !== programId) return false;
      if (orgId === 'none' ? !!r.organization_id : orgId !== 'all' && r.organization_id !== orgId) return false;
      if (account === 'registered' && !r.user_id) return false;
      if (account === 'visitor' && r.user_id) return false;
      if (cutoff && new Date(r.created_at).getTime() < cutoff) return false;
      return true;
    });
  }, [rows, search, programId, orgId, account, range]);

  const stats = useMemo(() => {
    const last30 = Date.now() - 30 * 24 * 60 * 60 * 1000;
    return {
      total: rows.length,
      registered: rows.filter(r => r.user_id).length,
      visitors: rows.filter(r => !r.user_id).length,
      recent: rows.filter(r => new Date(r.created_at).getTime() >= last30).length,
      routes: new Set(rows.map(r => r.program_id)).size,
    };
  }, [rows]);

  const byRoute = useMemo(() => {
    const map = new Map<string, { name: string; total: number; registered: number }>();
    filtered.forEach(r => {
      const cur = map.get(r.program_id) ?? { name: r.program_name, total: 0, registered: 0 };
      cur.total += 1;
      if (r.user_id) cur.registered += 1;
      map.set(r.program_id, cur);
    });
    return Array.from(map.values()).sort((a, b) => b.total - a.total);
  }, [filtered]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const pageRows = filtered.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);

  const resetPage = <T,>(setter: (v: T) => void) => (v: T) => {
    setter(v);
    setPage(0);
  };

  const handleExport = () => {
    const blob = new Blob(['\uFEFF' + toCsv(filtered)], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `interesados-rutas-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const kpis = [
    { label: 'Total interesados', value: stats.total, icon: Users, color: 'text-primary' },
    { label: 'Con cuenta', value: stats.registered, icon: UserCheck, color: 'text-emerald-600' },
    { label: 'Visitantes', value: stats.visitors, icon: UserPlus, color: 'text-amber-600' },
    { label: 'Últimos 30 días', value: stats.recent, icon: CalendarClock, color: 'text-blue-600' },
    { label: 'Rutas con interés', value: stats.routes, icon: GraduationCap, color: 'text-violet-600' },
  ];

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Interesados</h1>
            <p className="text-muted-foreground">
              Todas las personas que pulsaron “Estoy Interesado” en cualquier ruta, de todas las empresas.
            </p>
          </div>
          <Button variant="outline" onClick={handleExport} disabled={filtered.length === 0}>
            <Download className="w-4 h-4 mr-2" />
            Exportar CSV
          </Button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {kpis.map(k => (
            <Card key={k.label}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">{k.label}</p>
                    <p className="text-2xl font-bold">{isLoading ? '—' : k.value}</p>
                  </div>
                  <k.icon className={`w-5 h-5 ${k.color}`} />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
              <div className="relative md:col-span-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  className="pl-9"
                  placeholder="Nombre o correo"
                  value={search}
                  onChange={e => resetPage(setSearch)(e.target.value)}
                />
              </div>
              <Select value={programId} onValueChange={resetPage(setProgramId)}>
                <SelectTrigger><SelectValue placeholder="Ruta" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las rutas</SelectItem>
                  {programOptions.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={orgId} onValueChange={resetPage(setOrgId)}>
                <SelectTrigger><SelectValue placeholder="Empresa" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las empresas</SelectItem>
                  <SelectItem value="none">Independientes</SelectItem>
                  {orgOptions.map(o => (
                    <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={account} onValueChange={v => resetPage(setAccount)(v as AccountFilter)}>
                <SelectTrigger><SelectValue placeholder="Cuenta" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Cualquier cuenta</SelectItem>
                  <SelectItem value="registered">Registrados</SelectItem>
                  <SelectItem value="visitor">Visitantes</SelectItem>
                </SelectContent>
              </Select>
              <Select value={range} onValueChange={v => resetPage(setRange)(v as RangeFilter)}>
                <SelectTrigger><SelectValue placeholder="Fecha" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todo el histórico</SelectItem>
                  <SelectItem value="7">Últimos 7 días</SelectItem>
                  <SelectItem value="30">Últimos 30 días</SelectItem>
                  <SelectItem value="90">Últimos 90 días</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {byRoute.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Interesados por ruta</CardTitle>
              <CardDescription>Según los filtros aplicados.</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Ruta</TableHead>
                    <TableHead className="text-right">Interesados</TableHead>
                    <TableHead className="text-right">Con cuenta</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {byRoute.map(r => (
                    <TableRow key={r.name}>
                      <TableCell className="font-medium">{r.name}</TableCell>
                      <TableCell className="text-right">{r.total}</TableCell>
                      <TableCell className="text-right text-muted-foreground">{r.registered}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              Listado ({filtered.length}
              {filtered.length !== rows.length ? ` de ${rows.length}` : ''})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : filtered.length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center">
                {rows.length === 0
                  ? 'Aún no hay interesados registrados en ninguna ruta.'
                  : 'Ningún interesado coincide con los filtros seleccionados.'}
              </p>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nombre</TableHead>
                      <TableHead>Correo</TableHead>
                      <TableHead>Celular</TableHead>
                      <TableHead>Ruta</TableHead>
                      <TableHead>Empresa</TableHead>
                      <TableHead>Cuenta</TableHead>
                      <TableHead>Fecha</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pageRows.map(r => (
                      <TableRow key={r.id}>
                        <TableCell className="font-medium">{r.full_name || '—'}</TableCell>
                        <TableCell>{r.email}</TableCell>
                        <TableCell>
                          {r.phone ? (
                            <a href={`https://wa.me/${r.phone.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline whitespace-nowrap">
                              {r.phone}
                            </a>
                          ) : '—'}
                        </TableCell>
                        <TableCell>{r.program_name}</TableCell>
                        <TableCell>{r.organization_name || 'Independiente'}</TableCell>
                        <TableCell>
                          {r.user_id ? (
                            <Badge variant="secondary">Registrado</Badge>
                          ) : (
                            <Badge variant="outline">Visitante</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {new Date(r.created_at).toLocaleDateString('es-CO')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {pageCount > 1 && (
                  <div className="flex items-center justify-between pt-4">
                    <p className="text-sm text-muted-foreground">
                      Página {safePage + 1} de {pageCount}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => Math.max(0, p - 1))}
                        disabled={safePage === 0}
                      >
                        Anterior
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => Math.min(pageCount - 1, p + 1))}
                        disabled={safePage >= pageCount - 1}
                      >
                        Siguiente
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
